import { Controller } from "@hotwired/stimulus";
import bootstrap from "bootstrap/dist/js/bootstrap";

export default class extends Controller {
  static targets = ["partialToUpdate", "modal", "table"];
  static values = {
    resourceName: String,
    sortColumn: Number,
    sortDirection: String,
    paging: Boolean,
    lengthChange: Boolean,
    pageLength: Number,
    searching: Boolean,
    ordering: Boolean,
    info: Boolean,
    stateSave: Boolean,
    autoWidth: Boolean,
    columnDefs: Array,
  };

  connect() {
    this.beforeCache = () => this.prepareForCache();
    document.addEventListener("turbolinks:before-cache", this.beforeCache);

    // DataTables detaches rows on page/search/sort. A tooltip showing on a row
    // that gets detached never receives its mouseleave, so it sticks to the
    // page. Bound on the element (not the table) to survive destroy/re-init.
    $(this.element).on("preDraw.dt", () => {
      this.hideTooltips();
    });

    // Defer init one frame so the browser can paint and process queued
    // clicks before DataTables does its synchronous sort/index work.
    this.initFrame = requestAnimationFrame(() => this.initializeDataTable());
  }

  disconnect() {
    document.removeEventListener("turbolinks:before-cache", this.beforeCache);
    $(this.element).off("preDraw.dt");
    if (this.initFrame) cancelAnimationFrame(this.initFrame);
    this.destroyExistingDataTable();
  }

  hideTooltips() {
    this.element
      .querySelectorAll('[data-bs-toggle="tooltip"]')
      .forEach((el) => bootstrap.Tooltip.getInstance(el)?.hide());
  }

  // Turbolinks clones the body 1ms after `turbolinks:before-cache`, while
  // Stimulus does not disconnect until the swap. Two things therefore rot into
  // the cached snapshot unless they are undone here, synchronously:
  //   - the DataTables-enhanced markup, holding only the attached page of rows,
  //     which Back then re-initializes on top of itself;
  //   - tip nodes, because the global handler's `hide()` fades for 150ms and so
  //     is still mid-flight, leaving an invisible click blocker on Back.
  // Popovers render as `.popover`, so the sweep leaves them alone.
  prepareForCache() {
    this.hideTooltips();
    this.destroyExistingDataTable();
    document
      .querySelectorAll('.tooltip[role="tooltip"]')
      .forEach((tip) => tip.remove());
  }

  updateHtml(event) {
    const [_data, _status, xhr] = event.detail;
    this.partialToUpdateTarget.innerHTML = xhr.response;
  }

  initializeDataTable() {
    try {
      const table = this.tableTarget;

      if (!table) {
        console.warn("Table target not found");
        return;
      }

      if ($.fn.DataTable.isDataTable(table)) {
        $(table).DataTable().destroy();
      }

      const order =
        this.hasSortColumnValue && this.hasSortDirectionValue
          ? [[this.sortColumnValue, this.sortDirectionValue]]
          : [];

      // Booleans use the has*Value guard: `this.pagingValue || true` can never
      // be false, which silently ignored every `*-value="false"` in the views.
      const config = {
        paging: this.hasPagingValue ? this.pagingValue : true,
        pageLength: this.pageLengthValue || 100,
        lengthChange: this.hasLengthChangeValue ? this.lengthChangeValue : true,
        searching: this.hasSearchingValue ? this.searchingValue : true,
        ordering: this.hasOrderingValue ? this.orderingValue : true,
        info: this.hasInfoValue ? this.infoValue : false,
        stateSave: this.hasStateSaveValue ? this.stateSaveValue : false,
        autoWidth: this.hasAutoWidthValue ? this.autoWidthValue : true,
        order: order,
      };
      // Only set when a view passes them: DataTables treats an empty array as
      // "no per-column config", but the key still has to be absent for the
      // defaults to apply cleanly.
      if (this.hasColumnDefsValue) config.columnDefs = this.columnDefsValue;

      $(table).DataTable(config);
      this.pinDropdowns();
    } catch (error) {
      console.error(`Error initializing DataTable:`, error);
    } finally {
      // The view reserves the control bands' height from the first paint; the
      // reservation comes off as DataTables fills that space, so the table
      // never moves. Removed in `finally` so a failed init doesn't strand it.
      this.element.classList.remove("dt-reserve", "dt-reserve--footer");
    }
  }

  // Row-action menus sit inside a horizontally scrollable wrapper, which clips
  // an absolutely positioned menu. Bootstrap exposes Popper's fixed strategy
  // only through `popperConfig`, so the instances have to be created here
  // rather than by the click data-api.
  pinDropdowns() {
    this.element
      .querySelectorAll('[data-bs-toggle="dropdown"]')
      .forEach((toggle) =>
        bootstrap.Dropdown.getOrCreateInstance(toggle, {
          popperConfig: (defaultConfig) => ({
            ...defaultConfig,
            strategy: "fixed",
          }),
        })
      );
  }

  destroyExistingDataTable() {
    try {
      const table = this.tableTarget;
      if ($.fn.DataTable.isDataTable(table)) {
        $(table).DataTable().destroy();
      }
    } catch (error) {
      console.error("Error destroying DataTable:", error);
    }
  }

  openModal(event) {
    const modal = bootstrap.Modal.getOrCreateInstance(this.modalTarget);

    if (modal) {
      modal.show();
    }
    this.updateHtml(event);
  }

  closeModal() {
    const modal = bootstrap.Modal.getInstance(this.modalTarget);
    if (modal) {
      modal.hide();
    }
  }

  processResponseRow(responseHtml) {
    const tempTable = document.createElement("table");
    tempTable.innerHTML = responseHtml;
    return tempTable.querySelector("tr");
  }

  updateTable(event) {
    const [_data, _status, xhr] = event.detail;
    const newRow = this.processResponseRow(xhr.response);
    if (!newRow) return;

    const rowId = newRow.id;
    const existingRow = document.getElementById(rowId);

    try {
      this.destroyExistingDataTable();

      if (existingRow) {
        existingRow.innerHTML = newRow.innerHTML;
      } else {
        const tableBody = this.tableTarget.querySelector("tbody");
        if (tableBody) {
          tableBody.insertBefore(newRow, tableBody.firstChild);
          this.updateCountBadge(1);
        }
      }

      this.initializeDataTable();
    } catch (error) {
      console.error("Error updating table:", error);
      if (existingRow) {
        existingRow.outerHTML = newRow.outerHTML;
      } else {
        const tableBody = this.tableTarget.querySelector("tbody");
        if (tableBody) {
          tableBody.insertBefore(newRow, tableBody.firstChild);
        }
      }
    }

    this.closeModal();
  }

  appendRow(event) {
    const [_data, _status, xhr] = event.detail;
    const newRow = this.processResponseRow(xhr.response);

    try {
      this.destroyExistingDataTable();

      const tableBody = this.tableTarget.querySelector("tbody");
      if (tableBody) {
        tableBody.insertBefore(newRow, tableBody.firstElementChild);
        this.updateCountBadge(1);
      }

      this.initializeDataTable();
    } catch (error) {
      console.error("Error appending row:", error);
      const tableBody = this.tableTarget.querySelector("tbody");
      if (tableBody) {
        tableBody.insertBefore(newRow, tableBody.firstElementChild);
      }
    }

    this.closeModal();
  }

  updateCountBadge(change) {
    const countBadge = document.querySelector(".badge.bg-primary.rounded-pill");
    if (countBadge) {
      const currentCount = parseInt(countBadge.textContent.trim());
      if (!isNaN(currentCount)) {
        countBadge.textContent = (currentCount + change).toString();
      }
    }
  }

  /**
   * After DELETE (or similar), server returns 200 with X-Removed-Row-Id header
   * matching the <tr id="...">. Removes the row without re-rendering the whole table.
   */
  removeRow(event) {
    const detail = event.detail;
    const xhr = Array.isArray(detail) ? detail[2] : detail;
    const link = event.currentTarget;
    let rowId = link.dataset.removedRowId;
    if (xhr && typeof xhr.getResponseHeader === "function") {
      rowId = xhr.getResponseHeader("X-Removed-Row-Id") || rowId;
    }
    if (!rowId) return;

    const row = document.getElementById(rowId);
    if (!row) return;

    try {
      this.destroyExistingDataTable();
      row.remove();
      this.updateCountBadge(-1);
      this.initializeDataTable();
    } catch (error) {
      console.error("Error removing row:", error);
      row.remove();
      this.updateCountBadge(-1);
    }
  }
}
